import { formatDate, formatMoney, toMoney } from '@betterment/core';
import { Box } from '@betterment/samba-react';
import { Button, Flex, HR, ListRow, Modal, Text } from '@betterment/style-closet-react';

import Amount from './Amount';
import Availability from './Availability';
import CategoryDropdown from './CategoryDropdown';
import { HandleCategoryUpdate, SetAccountRuleElgibileCategory, SetActiveModal } from './data';

import { Flash } from '../../../Shared/contexts/FlashContext';
import { DetailedTransaction, TransactionStatus } from '../../data';
import {
  isCheckTransaction as _isCheckTransaction,
  getCashbackProperties,
  getCheckImageUrls,
  getDisplayStatus,
  showEstimatedAvailability,
} from '../../models/detailed-transaction';
import { useCustomer } from '../CustomerProvider';
import TransactionIcon from '../TransactionIcon';

const CheckImage: React.FC<React.PropsWithChildren<{ checkUrl: string; title: string; text: string }>> = ({
  checkUrl,
  title,
  text,
}) => (
  <Flex flexDirection="column" alignItems="center">
    <Box
      css={{
        border: '1px solid',
        borderRadius: '4px',
        borderColor: 'grey.10',
        minHeight: '100px',
        width: '100%',
        marginBottom: 'space.200',
      }}
    >
      <img src={checkUrl} alt={title} />
    </Box>
    <a href={checkUrl} target="_blank" rel="noopener noreferrer" title={title}>
      <Text color="inherit">{text}</Text>
    </a>
  </Flex>
);

const FootNote: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <Box
    css={{
      paddingInline: {
        base: 'space.0',
        sm: 'space.200',
      },

      paddingTop: 'space.200',
    }}
  >
    <Text fontSize={1} color="bmtGrey50">
      {children}
    </Text>
  </Box>
);

interface Props {
  transaction: DetailedTransaction;
  handleCancel: () => void;
  onClose: () => void;
  setActiveModal: SetActiveModal;
  setAccountRuleElgibileCategory: SetAccountRuleElgibileCategory;
  handleCategoryUpdate: HandleCategoryUpdate;
}

const TransactionModalDetail: React.FC<React.PropsWithChildren<Props>> = ({
  transaction,
  handleCancel,
  onClose,
  setActiveModal,
  setAccountRuleElgibileCategory,
  handleCategoryUpdate,
}) => {
  const {
    amountCents,
    isCredit,
    rawDescription,
    bettermentCategory = 'Uncategorized',
    category = 'Uncategorized',
    earnedCashbackRewardsCents,
    pendingCashbackRewardsCents,
    createdAt,
    status,
    cancelability,
    type,
    icon,
  } = transaction;
  const { customer } = useCustomer();
  const typeDescription = type.description.toLowerCase();
  const isAtmWithdrawal = bettermentCategory === 'ATM Withdrawal';
  const isInternalTransfer = bettermentCategory === 'Internal Transfer';

  const isCancelable = cancelability.eligible && !cancelability.expired;
  const displayStatus = getDisplayStatus(status);
  const { cashbackStatus, cashbackAmount, cashbackMessage } = getCashbackProperties(
    customer,
    earnedCashbackRewardsCents,
    pendingCashbackRewardsCents
  );
  const isCheckTransaction = _isCheckTransaction(transaction);
  const { checkFrontImageUrl, checkBackImageUrl } = getCheckImageUrls(transaction);

  return (
    <Modal
      label="transaction details"
      contentWidth={['100%', '480px']}
      closeHandler={onClose}
      variant="new"
      data-testid="transaction-details-modal"
    >
      <Box
        css={{
          paddingBlock: {
            base: 'space.200',
            sm: 'space.300',
          },

          paddingInline: {
            base: 'space.200',
            sm: 'space.100',
          },
        }}
      >
        <Flex py={9} alignItems="center" flexDirection="column">
          <TransactionIcon icon={icon} category={category} size="large" />
          <Text fontSize="6" lineHeight="5" py={6}>
            <Amount status={status} isCredit={isCredit} amountCents={amountCents} />
          </Text>
          <Text textAlign="center">{rawDescription}</Text>
        </Flex>
        <Box
          css={{
            paddingBottom: 'space.400',
          }}
        >
          <HR variant="list-row" />
          <ListRow
            leadingTitle="Date"
            trailingTitle={formatDate(createdAt, 'longOrdinalShortMonth')}
            variant="expanded"
          />
          <HR variant="list-row" />
          <ListRow leadingTitle="Status" trailingTitle={displayStatus} variant="expanded" />
          <HR variant="list-row" />
          {isInternalTransfer && status === TransactionStatus.Pending ? (
            <ListRow leadingTitle="Category" trailingTitle={category} variant="expanded" />
          ) : (
            <CategoryDropdown
              transaction={transaction}
              setActiveModal={setActiveModal}
              setAccountRuleElgibileCategory={setAccountRuleElgibileCategory}
              handleCategoryUpdate={handleCategoryUpdate}
            />
          )}
          <HR variant="list-row" />
          {showEstimatedAvailability(transaction) && (
            <>
              <Availability transaction={transaction} />
              <HR variant="list-row" />
            </>
          )}
          {isCancelable && (
            <>
              <ListRow variant="standard">
                <ListRow.Slot name="leading">
                  <Button variant="tertiary" onClick={handleCancel}>
                    Cancel {typeDescription}
                  </Button>
                </ListRow.Slot>
              </ListRow>
              <HR variant="list-row" />
              <FootNote>
                To cancel this {typeDescription}, you must do so by {formatDate(cancelability.deadline)} at 3PM ET.
              </FootNote>
            </>
          )}
          {isAtmWithdrawal && <FootNote>Fees are typically reimbursed within one business day.</FootNote>}
          {isCheckTransaction &&
            (checkFrontImageUrl && checkBackImageUrl ? (
              <Flex px={[8, 8, 9]} pt="8">
                <Flex.Item mr="6" flex="1">
                  <CheckImage checkUrl={checkFrontImageUrl} text="View front" title="Check Front Image" />
                </Flex.Item>
                <Flex.Item flex="1">
                  <CheckImage checkUrl={checkBackImageUrl} text="View back" title="Check Back Image" />
                </Flex.Item>
              </Flex>
            ) : (
              <FootNote>Check images will appear here once the check has settled.</FootNote>
            ))}
          {cashbackStatus && (
            <>
              <ListRow
                variant="expanded"
                verticalAlign="top"
                leadingTitle={`Cash back ${cashbackStatus}`}
                leadingSubtitle={cashbackMessage}
              >
                <ListRow.Slot name="trailing">
                  <Text color="bmtTeal90">{formatMoney(toMoney(cashbackAmount))}</Text>
                </ListRow.Slot>
              </ListRow>
              <HR variant="list-row" />
            </>
          )}
          <Flash mx="4" />
        </Box>
      </Box>
    </Modal>
  );
};

export default TransactionModalDetail;
